// src/utils/GeoLocationNotifier.js

import React, { useEffect, useState } from 'react';
import { Marker } from 'react-leaflet';
//import L from 'leaflet';
import * as turf from '@turf/turf';

/**
 * Компонент, который:
 * 1) Каждые 10 секунд обновляет геолокацию пользователя
 * 2) Если пользователь внутри bounds, ищет ближайшую улицу в roadsData
 * 3) Вне bounds => "Вне границ Sea Breeze Resort"
 * 4) Вызывает колбэки onPositionUpdate и onStreetNameUpdate
 */

function GeoLocationNotifier({
                                 bounds,
                                 roadsData,
                                 userIcon,
                                 onPositionUpdate,
                                 onStreetNameUpdate,
                             }) {
    //const map = useMap();
    const [userPosition, setUserPosition] = useState(null);

    useEffect(() => {
        // Функция, которая обращается к navigator.geolocation
        const updatePosition = () => {
            if (!navigator.geolocation) {
                console.error('Геолокация не поддерживается вашим браузером.');
                if (onStreetNameUpdate) {
                    onStreetNameUpdate('sea breeze resort');
                }
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const lat = pos.coords.latitude;
                    const lng = pos.coords.longitude;
                    const newPosition = [lat, lng];
                    setUserPosition(newPosition);

                    // Колбэк в App: обновить позицию
                    if (onPositionUpdate) {
                        onPositionUpdate(newPosition);
                    }

                    // Если пользователь вне заданных границ
                    if (!bounds.contains(newPosition)) {
                        onStreetNameUpdate && onStreetNameUpdate('Вы вне границ Sea Breeze');
                        return;
                    }

                    // Если внутри границ, пробуем найти ближайшую улицу
                    if (roadsData) {
                        const nearestStreet = getNearestStreetName(lat, lng, roadsData);
                        if (nearestStreet) {
                            onStreetNameUpdate && onStreetNameUpdate(nearestStreet);
                        } else {
                            onStreetNameUpdate && onStreetNameUpdate('Sea Breeze Resort');
                        }
                    } else {
                        onStreetNameUpdate && onStreetNameUpdate('Загрузка...');
                    }
                },
                (error) => {
                    console.error('Ошибка определения местоположения:', error);
                    onStreetNameUpdate && onStreetNameUpdate('Sea Breeze Resort');
                },
                { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
            );
        };

        // Первый вызов сразу после рендера
        updatePosition();

        // Запускаем интервал (каждые 5 секунд)
        const intervalId = setInterval(updatePosition, 10000);

        // Очищаем интервал при размонтировании
        return () => clearInterval(intervalId);
    }, [bounds, roadsData, onPositionUpdate, onStreetNameUpdate]);

    // Вспомогательная функция: поиск ближайшей улицы с помощью turf.js
    function getNearestStreetName(lat, lng, geoJson) {
        const userPoint = turf.point([lng, lat]);
        let nearestStreet = null;
        let minDistance = Infinity;
        const THRESHOLD_METERS = 50;

        geoJson.features.forEach((feature) => {
            // Проверяем, есть ли у линии geometry и name
            if (!feature.geometry || !feature.properties?.name) return;

            // Проверяем тип геометрии
            if (feature.geometry.type === 'LineString') {
                const line = turf.lineString(feature.geometry.coordinates);
                const distance = turf.pointToLineDistance(userPoint, line, { units: 'meters' });
                if (distance < minDistance) {
                    minDistance = distance;
                    nearestStreet = feature.properties.name;
                }
            } else if (feature.geometry.type === 'MultiLineString') {
                const multiLine = turf.multiLineString(feature.geometry.coordinates);
                const distance = turf.pointToLineDistance(userPoint, multiLine, { units: 'meters' });
                if (distance < minDistance) {
                    minDistance = distance;
                    nearestStreet = feature.properties.name;
                }
            }
        });

        // Если улица ближе, чем THRESHOLD_METERS, возвращаем её
        if (minDistance < THRESHOLD_METERS && nearestStreet) {
            return nearestStreet;
        }
        return null;
    }

    // Отрисовываем маркер, если позиция уже определена
    return userPosition ? (
        <Marker
            position={userPosition}
            icon={userIcon}
        />
    ) : null;
}

export default GeoLocationNotifier;
